import { put, call, takeEvery, takeLatest } from "redux-saga/effects";
import * as actions from "./actions";
import * as constants from "./constants";
import { toast } from "react-toastify";
import * as service from "./service";

function* fetchDecisionsSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.fetchDecisions, action.params);
        yield put(actions.fetchDecisionsSuccess(response.data));
    } catch (error) {
        yield put(actions.fetchDecisionsFailed(error));
        yield call(toast.error, "Erreur lors du chargement des décisions.");
    }
}

function* fetchDecisionDetailSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.fetchDecisionDetail, action.id);
        yield put(actions.fetchDecisionDetailSuccess(response.data));
    } catch (error: any) {
        yield put(actions.fetchDecisionDetailFailed(error));
        const data = error?.response?.data;
        if (data?.detail) {
            yield call(toast.error, data.detail);
        } else {
            yield call(toast.error, "Erreur lors du chargement de la décision.");
        }
    }
}

function* createDecisionSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.createDecision, action.payload);
        yield put(actions.createDecisionSuccess(response.data));
        yield call(toast.success, "Décision créée avec succès.");
        if (action.callback) action.callback();
    } catch (error: any) {
        yield put(actions.createDecisionFailed(error));
        const data = error?.response?.data;
        if (data?.matiere) {
            yield call(toast.error, data.matiere[0]);
        } else {
            yield call(toast.error, "Erreur lors de la création de la décision.");
        }
    }
}

function* updateDecisionSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.updateDecision, action.id, action.payload);
        yield put(actions.updateDecisionSuccess(response.data));
        yield call(toast.success, "Décision modifiée avec succès.");
        if (action.callback) action.callback();
    } catch (error: any) {
        yield put(actions.updateDecisionFailed(error));
        const data = error?.response?.data;
        const status = error?.response?.status;
        if (data?.detail) {
            yield call(toast.error, data.detail);
        } else if (status === 403) {
            yield call(toast.error, "Vous n'avez pas la permission d'effectuer cette action.");
        } else {
            yield call(toast.error, "Erreur lors de la modification.");
        }
    }
}

function* deleteDecisionSaga(action: any): Generator<any, void, any> {
    try {
        yield call(service.deleteDecision, action.id);
        yield put(actions.deleteDecisionSuccess(action.id));
        yield call(toast.success, "Décision supprimée.");
        if (action.callback) action.callback();
    } catch (error: any) {
        yield put(actions.deleteDecisionFailed(error));
        const data = error?.response?.data;
        if (data?.detail) {
            yield call(toast.error, data.detail);
        } else {
            yield call(toast.error, "Erreur lors de la suppression.");
        }
    }
}

function* uploadDecisionsSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.uploadDecisions, action.files);
        // API returns 202 with { resultats: [{ success, decision_id, task_id, statut, pipeline, polling_url }] }
        const results = response.data.resultats || response.data || [];
        yield put(actions.uploadDecisionsSuccess(results));

        const successCount = results.filter((r: any) => r.success).length;
        const failCount = results.filter((r: any) => !r.success).length;

        if (failCount === 0) {
            yield call(toast.success, `${successCount} fichier(s) envoyé(s). Pipeline en cours...`);
        } else if (successCount === 0) {
            yield call(toast.error, `Échec de l'upload de ${failCount} fichier(s).`);
        } else {
            yield call(toast.warning, `${successCount} envoyé(s), ${failCount} en erreur.`);
        }

        if (action.callback) action.callback(results);
    } catch (error) {
        yield put(actions.uploadDecisionsFailed(error));
        yield call(toast.error, "Erreur lors de l'upload des fichiers.");
    }
}

const workflowLabels: Record<string, string> = {
    soumettre: 'soumise pour validation',
    valider: 'validée',
    publier: 'publiée',
    rejeter: 'rejetée',
    anonymiser: 'anonymisée',
};

function* workflowActionSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.workflowAction, action.id, action.action);
        yield put(actions.workflowActionSuccess(response.data));
        yield call(toast.success, `Décision ${workflowLabels[action.action] || 'mise à jour'}.`);
        if (action.callback) action.callback();
    } catch (error: any) {
        yield put(actions.workflowActionFailed(error));
        const data = error?.response?.data;
        if (data?.error) {
            yield call(toast.error, data.error);
        } else if (error?.response?.status === 502) {
            yield call(toast.error, "Service d'anonymisation indisponible. Réessayez plus tard.");
        } else {
            yield call(toast.error, "Erreur lors de l'action sur la décision.");
        }
    }
}

function* fetchStatsSaga(): Generator<any, void, any> {
    try {
        const response = yield call(service.fetchStats);
        yield put(actions.fetchStatsSuccess(response.data));
    } catch (error) {
        yield put(actions.fetchStatsFailed(error));
    }
}

function* fetchAdminDashboardSaga(): Generator<any, void, any> {
    try {
        const response = yield call(service.fetchAdminDashboard);
        yield put(actions.fetchAdminDashboardSuccess(response.data));
    } catch (error) {
        yield put(actions.fetchAdminDashboardFailed(error));
    }
}

function* fetchPipelineSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.fetchPipeline, action.params);
        yield put(actions.fetchPipelineSuccess(response.data));
    } catch (error) {
        yield put(actions.fetchPipelineFailed(error));
    }
}

function* reassignDecisionsSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.reassignDecisions, action.payload);
        yield put(actions.reassignDecisionsSuccess(response.data));
        yield call(toast.success, "Décisions réassignées avec succès.");
        if (action.callback) action.callback();
    } catch (error: any) {
        yield put(actions.reassignDecisionsFailed(error));
        const data = error?.response?.data;
        if (data?.detail) {
            yield call(toast.error, data.detail);
        } else {
            yield call(toast.error, "Erreur lors de la réassignation.");
        }
    }
}

function* fetchSuperAdminDashboardSaga(): Generator<any, void, any> {
    try {
        const [dashboard, organisations, statistiques] = yield Promise.all([
            call(service.fetchSuperAdminDashboard),
            call(service.fetchSuperAdminOrganisations),
            call(service.fetchSuperAdminStatistiques),
        ]);

        const combined = {
            ...dashboard.data,
            organisations: organisations.data,
            ...statistiques.data,
        };

        yield put(actions.fetchSuperAdminDashboardSuccess(combined));
    } catch (error) {
        yield put(actions.fetchSuperAdminDashboardFailed(error));
        yield call(toast.error, "Erreur lors du chargement des statistiques.");
    }
}

export default function* decisionsSaga() {
    yield takeLatest(constants.FETCH_DECISIONS_REQUESTING, fetchDecisionsSaga);
    yield takeLatest(constants.FETCH_DECISION_DETAIL_REQUESTING, fetchDecisionDetailSaga);
    yield takeEvery(constants.CREATE_DECISION_REQUESTING, createDecisionSaga);
    yield takeEvery(constants.UPDATE_DECISION_REQUESTING, updateDecisionSaga);
    yield takeEvery(constants.DELETE_DECISION_REQUESTING, deleteDecisionSaga);
    yield takeEvery(constants.UPLOAD_DECISIONS_REQUESTING, uploadDecisionsSaga);
    yield takeEvery(constants.WORKFLOW_ACTION_REQUESTING, workflowActionSaga);
    yield takeLatest(constants.FETCH_STATS_REQUESTING, fetchStatsSaga);
    yield takeLatest(constants.FETCH_ADMIN_DASHBOARD_REQUESTING, fetchAdminDashboardSaga);
    yield takeLatest(constants.FETCH_PIPELINE_REQUESTING, fetchPipelineSaga);
    yield takeEvery(constants.REASSIGN_DECISIONS_REQUESTING, reassignDecisionsSaga);
    yield takeLatest(constants.FETCH_SUPERADMIN_DASHBOARD_REQUESTING, fetchSuperAdminDashboardSaga);
}
